import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { STOCK_UNITS, type StockGroup, type StockUnit } from '@tailoring-crm/shared';
import { useCreateStockGroup, useUpdateStockGroup } from '../../lib/inventory/use-stock.js';
import { MediaUploader } from '../media/media-uploader.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Naming a heading, and saying what it is bought in.
 *
 * The unit here is labelled as a DEFAULT rather than as the group's measure, because that is
 * exactly what it is: it is copied onto an item once, when the item is created inside the group
 * without a unit of its own, and after that the item's unit is the only one any quantity is read
 * against. Changing it later moves nothing on any shelf, and the hint says so — a shop that
 * thought otherwise would edit this box expecting a hundred rows to restate themselves.
 */

/** The one field the media pipeline needs; opaque to it, and ours alone. */
const GROUP_MEDIA_ENTITY_TYPE = 'stock_group';

export interface StockGroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for an edit; absent to add a new heading. */
  group?: StockGroup | null;
  onSaved?: () => void;
}

export function StockGroupFormDialog({
  open,
  onOpenChange,
  group = null,
  onSaved,
}: StockGroupFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const isEdit = group !== null;
  const create = useCreateStockGroup();
  const update = useUpdateStockGroup();

  const [name, setName] = useState('');
  const [unit, setUnit] = useState<StockUnit>('metre');
  const [active, setActive] = useState(true);
  const [imageMediaId, setImageMediaId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? '');
    setUnit(group?.unit ?? 'metre');
    setActive(group?.active ?? true);
    setImageMediaId(group?.imageMediaId ?? null);
    setFormError(null);
  }, [open, group]);

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void (async () => {
      setFormError(null);
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        setFormError(t('stockGroups.nameRequired'));
        return;
      }

      try {
        if (isEdit) {
          await update.mutateAsync({
            groupId: group.id,
            input: { name: trimmed, unit, active, imageMediaId },
          });
        } else {
          await create.mutateAsync({ name: trimmed, unit, active, imageMediaId });
        }
        toast({
          title: isEdit ? t('stockGroups.saved') : t('stockGroups.created', { name: trimmed }),
          variant: 'success',
        });
        onSaved?.();
        onOpenChange(false);
      } catch (error) {
        setFormError(error instanceof Error ? error.message : t('error.unexpected'));
      }
    })();
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('stockGroups.editTitle') : t('stockGroups.addTitle')}
          </DialogTitle>
          <DialogDescription>{t('stockGroups.formDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-name`}>{t('stockGroups.nameLabel')}</Label>
            <Input
              id={`${fieldId}-name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-unit`}>{t('stockGroups.unitLabel')}</Label>
            <select
              id={`${fieldId}-unit`}
              value={unit}
              onChange={(event) => setUnit(event.target.value as StockUnit)}
              aria-describedby={`${fieldId}-unit-hint`}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {STOCK_UNITS.map((one) => (
                <option key={one} value={one}>
                  {t(`stock.unit.${one}`)}
                </option>
              ))}
            </select>
            <p id={`${fieldId}-unit-hint`} className="text-sm text-muted-foreground">
              {t('stockGroups.unitHint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t('stockGroups.imageLabel')}</span>
            <MediaUploader
              entityType={GROUP_MEDIA_ENTITY_TYPE}
              entityId={group?.id ?? null}
              maxFiles={1}
              onChange={(media) => setImageMediaId(media[0]?.id ?? null)}
            />
          </div>

          <div className="flex items-start gap-3 text-sm">
            <input
              id={`${fieldId}-active`}
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-primary"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            <div className="flex flex-col gap-0.5">
              <label htmlFor={`${fieldId}-active`} className="font-medium">
                {t('stockGroups.activeLabel')}
              </label>
              <p className="text-muted-foreground">{t('stockGroups.activeHint')}</p>
            </div>
          </div>

          {formError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {formError}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {isEdit ? t('common.save') : t('stockGroups.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
